import { useEffect, useRef, useState, type ReactNode } from 'react';
import { acceptInvitation } from '@/api/endpointsMembers';
import { useAuth } from '@/auth/AuthProvider';
import { Icon } from '@/components/Icon';
import { AppMark } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { acceptViewOf, tokenFromHash, type AcceptView } from '@/features/members/membersModel';

/** Survives the sign-in redirect; cleared once the API answered. Never in the URL after the first load. */
const TOKEN_KEY = 'hangar.invitation-token';

function storage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** The token of `#token=…` (moved into session storage and stripped from the address bar), or the stored one. */
function takeToken(): string | undefined {
  const fromHash = tokenFromHash(window.location.hash);
  if (fromHash) {
    storage()?.setItem(TOKEN_KEY, fromHash);
    window.history.replaceState(null, '', window.location.pathname);
    return fromHash;
  }
  return storage()?.getItem(TOKEN_KEY) ?? undefined;
}

/** Accepts once the person is signed in; `null` while there is nothing to show yet. */
function useAcceptance(token: string | undefined, signedIn: boolean): AcceptView | null {
  const [view, setView] = useState<AcceptView | null>(null);
  const started = useRef(false);
  useEffect(() => {
    if (!token || !signedIn || started.current) return;
    started.current = true;
    acceptInvitation(token)
      .then((r) => setView(acceptViewOf({ orgName: r.org.name, role: r.orgRole })))
      .catch((error: unknown) => setView(acceptViewOf({ error })))
      .finally(() => storage()?.removeItem(TOKEN_KEY));
  }, [token, signedIn]);
  return view;
}

/**
 * `/invitations/accept#token=…`, the link of the invitation email. Signs the person in (the invited address), then
 * accepts: the API makes them a member of the org. Outside the app shell: they belong to no org until it answers.
 */
export function AcceptInvitationPage() {
  const { status, signIn } = useAuth();
  const [token] = useState(takeToken);
  const view = useAcceptance(token, status === 'signed-in');
  let body: ReactNode;
  if (!token) body = <Outcome icon="link_off" tone="error" title="This link is incomplete" text="Open the link from the invitation email again; copying it by hand can cut it short." />;
  else if (status === 'loading' || (status === 'signed-in' && !view)) body = <Pending />;
  else if (status === 'signed-out') body = <SignInFirst onSignIn={() => void signIn({ redirect: '/invitations/accept' })} />;
  else body = <Result view={view!} />;
  return (
    <main data-testid="accept-page" className="flex min-h-dvh items-center justify-center bg-background p-4">
      <Card className="enter w-full max-w-md gap-5 p-6 sm:p-8">
        <div className="flex items-center gap-2.5">
          <AppMark />
          <span className="text-sm font-semibold text-foreground">Hangar</span>
        </div>
        {body}
      </Card>
    </main>
  );
}

function Pending() {
  return (
    <div data-testid="accept-pending" aria-busy="true" className="flex flex-col gap-3">
      <Skeleton className="h-6 w-3/4" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-11 w-40" />
    </div>
  );
}

function SignInFirst({ onSignIn }: { onSignIn: () => void }) {
  return (
    <div data-testid="accept-sign-in" className="flex flex-col gap-4">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-foreground">You're invited to Hangar</h1>
        <p className="mt-1.5 text-sm text-muted-foreground">Sign in with the email address the invitation was sent to. The invitation is accepted as soon as you are signed in.</p>
      </div>
      <Button data-testid="accept-sign-in-button" onClick={onSignIn} className="h-11 self-start">
        <Icon name="login" size={18} />
        Sign in to accept
      </Button>
    </div>
  );
}

function Outcome({ icon, tone, title, text, action }: { icon: string; tone: 'ok' | 'error'; title: string; text: string; action?: ReactNode }) {
  return (
    <div data-testid={`accept-${tone}`} role={tone === 'error' ? 'alert' : 'status'} className="flex flex-col gap-4">
      <span className={tone === 'ok' ? 'flex size-11 items-center justify-center rounded-lg border border-primary/30 bg-primary/10 text-primary' : 'flex size-11 items-center justify-center rounded-lg border border-destructive/30 bg-destructive/10 text-destructive'}>
        <Icon name={icon} size={24} />
      </span>
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-foreground">{title}</h1>
        <p className="mt-1.5 text-sm text-muted-foreground">{text}</p>
      </div>
      {action}
    </div>
  );
}

/** A full load, so the console starts over with the new membership (`GET /me`). */
const openHangar = <Button data-testid="accept-open" className="h-11 self-start" onClick={() => window.location.assign('/')}>Open Hangar</Button>;

function Result({ view }: { view: AcceptView }) {
  if (view.kind === 'joined') return <Outcome icon="check_circle" tone="ok" title={`Welcome to ${view.orgName}`} text={`You joined as ${view.role}. Your access applies from now on.`} action={openHangar} />;
  if (view.kind === 'already') return <Outcome icon="check_circle" tone="ok" title="You already accepted this invitation" text="Your membership is active: open Hangar to start." action={openHangar} />;
  return <Outcome icon="error" tone="error" title={view.title} text={view.description} />;
}
